import { useQuery } from "@tanstack/react-query";
import {
  controlService,
  siteService,
  cctvService,
  type Cctv,
  type AllSitesDashboard,
  type Site,
  type SiteDashboard,
} from "@safers/api";
import { cctvKeys } from "@/hooks/useCCTVStreams";

/** 대시보드는 실시간성이 있어야 하는데 STOMP 로 오는 건 이벤트뿐이라 주기 갱신한다. */
const REFRESH_MS = 60_000;

export const controlKeys = {
  all: ["control"] as const,
  national: () => ["control", "national"] as const,
  site: (siteId: number) => ["control", "site", siteId] as const,
  siteMeta: (siteId: number) => ["control", "site-meta", siteId] as const,
};

export interface ControlData {
  national: AllSitesDashboard | null;
  site: SiteDashboard | null;
  /** 현장 메타(이름·좌표) — 대시보드 응답에 없어 따로 받는다. */
  siteMeta: Site | null;
  siteMetaLoading: boolean;
  cctvs: Cctv[];
  cctvError: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * 관제 화면의 데이터.
 *
 * **셸이 소유한다.** 자식(패널)이 각자 받아 부모로 지도 좌표를 올려보내는 구조는
 * 방향이 뒤집혀 금방 꼬인다 — 스코프를 아는 쪽이 한 번에 받아 내려주는 편이 단순하다.
 *
 * 전국 집계는 스코프를 옮겨도 **버리지 않는다**(`staleTime` + 캐시). 현장에서 전국으로
 * 돌아올 때 다시 받으면 지도가 빈 채로 한 박자 멈춘다.
 *
 * 갱신은 보고 있는 스코프만 돈다 — 안 보는 쪽까지 돌리면 호출만 두 배가 된다.
 */
export function useControlData(siteId: number | null): ControlData {
  const national = useQuery({
    queryKey: controlKeys.national(),
    queryFn: () => controlService.getAllSites(),
    // 현장을 보는 동안에도 지도 POI 는 전국 데이터로 그리므로 캐시는 살려 두되,
    // 주기 갱신은 전국을 보고 있을 때만 한다.
    refetchInterval: siteId == null ? REFRESH_MS : false,
    staleTime: REFRESH_MS,
    refetchOnWindowFocus: "always",
  });

  const site = useQuery({
    queryKey: controlKeys.site(siteId ?? -1),
    queryFn: () => controlService.getSite(siteId as number),
    enabled: siteId != null,
    refetchInterval: REFRESH_MS,
    staleTime: REFRESH_MS,
    refetchOnWindowFocus: "always",
  });

  const siteMeta = useQuery({
    queryKey: controlKeys.siteMeta(siteId ?? -1),
    queryFn: () => siteService.get(siteId as number),
    enabled: siteId != null,
    // 관리자에서 바뀐 이름·좌표·모델은 탭 복귀 시 갱신한다.
    staleTime: Infinity,
    refetchOnWindowFocus: "always",
  });

  const cctvs = useQuery({
    queryKey: cctvKeys.streams(siteId ?? undefined),
    queryFn: () => cctvService.list(siteId as number),
    enabled: siteId != null,
    staleTime: REFRESH_MS,
    refetchOnWindowFocus: "always",
    refetchInterval: REFRESH_MS,
  });

  return {
    cctvs: siteId == null ? [] : (cctvs.data ?? []),
    cctvError: cctvs.isError,
    national: national.data ?? null,
    site: siteId == null ? null : (site.data ?? null),
    siteMeta: siteId == null ? null : (siteMeta.data ?? null),
    siteMetaLoading: siteId != null && siteMeta.isPending,
    // 전국이 아직 없을 때만 "로딩"이다. 현장 응답을 기다리느라 화면 전체를 덮으면
    // 스코프를 옮길 때마다 지도까지 사라져 in-place 전환이 무너진다.
    loading: national.isLoading,
    error: national.isError ? "대시보드를 불러오지 못했습니다" : null,
    reload: () => {
      void national.refetch();
      if (siteId != null) {
        void site.refetch();
        void siteMeta.refetch();
        void cctvs.refetch();
      }
    },
  };
}
